// taken from: https://github.com/eMarketer/em-jwt-reader/blob/master/src/index.js
import { LOGIN_TYPE__IP_USER, POD_NA1 } from '../constants/Constants';
import { jwtDecode } from './JwtDecoder';

/**
 * Reads the access token from the provided JWT access token cookie and returns an object with the decoded token information.
 * @param {string} accessTokenCookie - The access token cookie.
 * @returns {Object} - An object containing the decoded access token information.
 */
export function readAccessToken(accessTokenCookie: string) {
  const accessToken = jwtDecode(accessTokenCookie);
  const coalescedAccessToken = accessToken || {
    loginTypeId: '',
    jti: '',
    extranetId: '',
    pod: '',
    sub: '',
    given_name: '',
    exp: 0,
  };
  const authenticated = accessToken !== null;
  const isIpUser = authenticated && accessToken && accessToken.loginTypeId === LOGIN_TYPE__IP_USER;
  const pod = accessToken ? (accessToken.pod ? accessToken.pod : POD_NA1) : '';

  return {
    authenticated: authenticated,
    isIpUser: isIpUser,
    loginTypeId: coalescedAccessToken.loginTypeId,
    userId: coalescedAccessToken.jti,
    extranetId: coalescedAccessToken.extranetId,
    pod: pod.toLowerCase(),
    email: coalescedAccessToken.sub,
    firstName: coalescedAccessToken.given_name,
    expires: parseInt(coalescedAccessToken.exp, 10) || 0,
    tier: coalescedAccessToken.tier,
  };
}

/**
 * Checks if the provided access token has the specified role.
 * @param {string} accessToken - The JWT access token.
 * @param {string} role - The role to check.
 * @returns {boolean} - Returns true if the access token has the specified role, otherwise returns false.
 */
export function isInRole(accessToken: string, role: string) {
  const jwtPayload = jwtDecode(accessToken);

  if (!jwtPayload) return false;

  const coalescedRoles = jwtPayload.roles || '[]';

  try {
    const roles = JSON.parse(coalescedRoles);

    if (Array.isArray(roles)) {
      return roles.indexOf(role) > -1;
    }
  } catch (error) {
    // Invalid JSON in roles, return false
    return false;
  }

  return false;
}

/**
 * Retrieves a specific property value from the accessToken object.
 * @param {object} jwtCookie - The string from the cookie, which contains JWT accessToken object.
 * @param {string} propertyName - The name of the property to retrieve.
 * @returns {any} - The value of the specified property, or undefined if the property does not exist.
 */
export function getJwtProperty(jwtCookie: string, propertyName: string): any {
  if (!jwtCookie || !propertyName) return undefined;

  const jwtPayload = jwtDecode(jwtCookie);

  if (jwtPayload === null) return undefined;

  if (!jwtPayload || typeof jwtPayload !== 'object') {
    return undefined;
  }
  return jwtPayload[propertyName];
}
